import React, { useEffect, useState } from "react";

import { Clock, Loader2 } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";

import { FLOW } from "../../Container/AccountPlanContainer/flowCopy";
import { useMyPlanActivity } from "../../Hooks/useAccountPlan";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchAccountPlansEnabled } from "../../Store/Reducers/AccountPlanSlice";
import type { PlanActivityFinished } from "../../Types/AccountPlan";
import type { PlanActivityMineItem } from "../../Types/AccountPlan";
import { stateChip } from "../../Utils/AccountPlans/queueText";
import { TOAST_SLOT_ID, ToastCard, type ToastType } from "../Toast";

/** Signed-in routes live under /<org slug>/… (App.tsx); the tray sits inside that route. */
export const orgPath = (slug: string | undefined, path: string) =>
  slug ? `/${slug}${path}` : path;

const TOAST_TEXT: Record<
  PlanActivityFinished["event"],
  {
    title: (v: string) => string;
    body: string;
    /** The shared toast's colour (2026-11-04): ready green, needs review orange,
     * couldn't read red. */
    type: ToastType;
    /** The button and where it lands; default "Open plan" on the plan page. */
    open?: { label: string; query: string };
  }
> = {
  // Confirm plan (Phase 2): every read upload waits on the AE before any briefing.
  ready: {
    type: "success",
    title: () => "your upload is ready to confirm",
    body: "Check the slides we read. The briefing is written only after you confirm.",
    open: { label: "Take action", query: "?tab=viewer" }, // Confirm plan
  },
  ready_partial: {
    type: "success",
    // Read outcomes come before confirm, so never carry a number (D1).
    title: () => "your upload is ready to confirm, with gaps",
    body: "Some parts couldn't be produced. Check what we read, then confirm it.",
    open: { label: "Take action", query: "?tab=viewer" }, // Confirm plan
  },
  read_failed: {
    type: "error",
    title: () => "couldn't read your upload",
    body: "Open the plan to see why and try again.",
    // Sushant, 2026-10-31: the AE's step, named like the Plans list's button.
    open: { label: "Take action", query: "?tab=plan&focus=problem" },
  },
  rejected: {
    type: "warning",
    title: () => "your upload needs review",
    body: "A plan check failed. Open it to see why.",
    // Sushant, 2026-10-31: the AE's step, named like the Plans list's button.
    open: { label: "Take action", query: "?tab=plan&focus=problem" },
  },
  needs_review: {
    type: "warning",
    title: () => "your upload needs review",
    body: "Fix the deck and upload it again, or confirm it's right. No briefing is written until you do.",
    // Sushant, 2026-10-31: the AE's step, named like the Plans list's button.
    open: { label: "Take action", query: "?tab=plan&focus=problem" },
  },
  briefing_ready: {
    type: "success",
    title: v =>
      v ? `draft briefing for plan ${v} is ready` : "draft briefing is ready",
    body: "Review it, then send each section to its owner to verify. Ask about this plan opens once it is published.",
    // Sushant, 2026-10-31: the news is the briefing, so the button opens it;
    // named like the step it lands on (Sushant, 2026-11-04).
    open: { label: "Review briefing", query: "?tab=sponsor" },
  },
  // P8 (2026-11-03): no "couldn't be written · try again"; it reads as stalled.
  briefing_failed: {
    type: "warning",
    title: () => "the draft briefing is taking longer than usual",
    body: "Izba has been told. There is nothing for you to do.",
    open: { label: "Open briefing", query: "?tab=sponsor" },
  },
};

/**
 * Everywhere in ExecBridge (mounted once, next to the bell): a chip while any of my
 * plans is in line or processing, and a pop-up when one of them lands (spec NTF-05).
 * The bell keeps the same news for later; this is for people who are online now.
 */
/**
 * The pill's words (D40, Sushant 2026-10-31): the step, with no queue number or time
 * estimate, as the Plans list says it. Stalled and other states keep stateChip.
 */
export const pillLabel = (item: PlanActivityMineItem): string => {
  const stalled = item.queue?.read?.stalled || item.queue?.brief?.stalled;
  if (!stalled) {
    if (item.state === "in_line") return FLOW.queuedForParsing;
    if (item.state === "reading") return FLOW.parsing;
    if (item.state === "brief_in_line") return FLOW.briefingQueued;
    if (item.state === "briefing")
      return item.plan_version != null
        ? `${FLOW.writingBriefingFor} v${item.plan_version}`
        : FLOW.writingBriefingFor.replace(/ for$/, "");
  }
  return stateChip(item.state, item.queue).label;
};

const PlanActivityTray: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const enabled = useAppSelector(s => s.accountPlan.enabled);
  useEffect(() => {
    if (enabled === null) void dispatch(fetchAccountPlansEnabled());
  }, [enabled, dispatch]);
  const { active, toasts, dismiss } = useMyPlanActivity(enabled === true);

  // The app's toast column at the top right (Sushant, 2026-11-04): the plan
  // pop-ups join it below the app's own toasts. Without it (tests), a column of
  // our own in the same place.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById(TOAST_SLOT_ID));
  }, []);

  if (enabled !== true || (!active.length && !toasts.length)) return null;
  const first = active[0];
  const items = (
    <>
      {toasts.slice(-3).map(t => {
        const text = TOAST_TEXT[t.event];
        // The plan number only (D1); `version` is the internal upload number.
        const version = t.plan_version != null ? `v${t.plan_version}` : "";
        return (
          <ToastCard
            key={`${t.file_id}:${t.event}`}
            role="status"
            testId="plan-toast"
            type={text.type}
            icon={
              t.event === "briefing_failed" ? (
                <Clock aria-hidden="true" />
              ) : undefined
            }
            title={`${t.account_name || "Your account"}: ${text.title(version)}`}
            message={text.body}
            // These ask the AE to act, so they stay until dismissed: no countdown.
            action={{
              label: text.open?.label ?? "Open plan",
              onClick: () => {
                dismiss(t.file_id, t.event);
                navigate(
                  orgPath(
                    orgSlug,
                    `/account-plan/${t.account_id}${text.open?.query ?? ""}`
                  )
                );
              },
            }}
            onClose={() => dismiss(t.file_id, t.event)}
          />
        );
      })}
      {first && (
        <button
          type="button"
          data-testid="plan-tray-chip"
          // The plan page exists for every role that uploads (vendor admins have no
          // My Accounts, so the Activity view isn't a safe target for everyone).
          onClick={() =>
            navigate(
              orgPath(
                orgSlug,
                // One plan: reopen its progress dialog (PlanVersionsCard).
                active.length === 1
                  ? `/account-plan/${first.account_id}?tab=plan&progress=${first.file_id}`
                  : `/account-plan/${first.account_id}?tab=plan`
              )
            )
          }
          title={pillLabel(first)}
          className="block w-full border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <ToastCard
            type="info"
            compact
            role="status"
            icon={<Loader2 className="animate-spin" aria-hidden="true" />}
            title={
              active.length === 1
                ? `${first.account_name || "Plan"} · ${pillLabel(first)}`
                : `${active.length} plans processing`
            }
          />
        </button>
      )}
    </>
  );
  if (slot) return createPortal(items, slot);
  return createPortal(
    <div className="toast-wrapper" aria-live="polite">
      {items}
    </div>,
    document.body
  );
};

export default PlanActivityTray;
